import { useState } from 'react';
import {
  Bird,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Package,
  Truck,
  Warehouse,
  Weight,
  X,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCamadaActiva } from '@/lib/queries/camadas';
import { useContextoSalidas } from '@/lib/queries/salidas';
import { useLotesDeCamada } from '@/lib/queries/lotesNave';
import { useNaves } from '@/lib/queries/naves';
import { useAuthStore } from '@/stores/auth';
import { ModalOverlay } from './ModalOverlay';

/** Accesos al registro existente. La camada activa se obtiene de la API/copia local. */
export function RegistrationMenu({ onClose }: { onClose: () => void }) {
  const role = useAuthStore((s) => s.user?.rol);
  return role === 'operario' ? (
    <OperatorRegistrationMenu onClose={onClose} />
  ) : (
    <StandardRegistrationMenu onClose={onClose} />
  );
}

function OperatorRegistrationMenu({ onClose }: { onClose: () => void }) {
  const q = useContextoSalidas();
  const camada = q.data?.camada;
  const navigate = useNavigate();
  return (
    <ModalOverlay
      placement="bottom"
      onBackdrop={onClose}
      ariaLabelledby="register-title"
      className="max-w-md"
    >
      <header className="mb-2 flex items-center justify-between gap-3">
        <h2 id="register-title" className="ui-page-title">
          Registrar
        </h2>
        <button
          className="ui-icon-button ui-close-button"
          onClick={onClose}
          aria-label="Cerrar registro"
        >
          <X size={22} aria-hidden />
        </button>
      </header>
      {q.isLoading ? (
        <p role="status">Cargando camada…</p>
      ) : q.isError ? (
        <>
          <p role="alert">No se pudo cargar el contexto de salidas.</p>
          <button className="ui-secondary" onClick={() => void q.refetch()}>
            Reintentar
          </button>
        </>
      ) : !camada ? (
        <p className="py-4">No hay una camada abierta para registrar.</p>
      ) : (
        <>
          <p className="mb-4 text-sm text-text-secondary">
            <strong className="font-medium text-text-primary">{camada.codigo_lote}</strong> · Camada
            en curso
          </p>
          <button
            className="ui-primary mb-2 w-full"
            disabled={!q.data?.lotes.length}
            onClick={() => {
              onClose();
              navigate(`/camadas/${camada.id}/salidas/nueva`);
            }}
          >
            <Truck size={22} aria-hidden />
            Salida a matadero
            <ChevronRight size={18} aria-hidden />
          </button>
          <Link className="ui-menu-row" to={`/camadas/${camada.id}/salidas`} onClick={onClose}>
            Consultar salidas y pesos
            <ChevronRight size={18} aria-hidden />
          </Link>
        </>
      )}
    </ModalOverlay>
  );
}

function StandardRegistrationMenu({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const camadaQ = useCamadaActiva();
  const camada = camadaQ.data;
  const lotesQ = useLotesDeCamada(camada?.id ?? null);
  const navesQ = useNaves();
  const role = useAuthStore((s) => s.user?.rol);
  const canManageAnimals = role === 'admin' || role === 'ganadero';
  const [chooseNave, setChooseNave] = useState(false);

  function open(path: string, modal = true) {
    onClose();
    navigate(path, modal ? { state: { backgroundLocation: location } } : undefined);
  }

  const title = chooseNave ? 'Nave del muestreo' : 'Registrar';
  return (
    <ModalOverlay
      placement="bottom"
      onBackdrop={onClose}
      ariaLabelledby="register-title"
      className="max-w-md"
    >
      <header className="mb-2 flex items-center justify-between gap-3">
        <h2 id="register-title" className="ui-page-title">
          {title}
        </h2>
        <button
          type="button"
          className="ui-icon-button ui-close-button"
          onClick={onClose}
          aria-label="Cerrar registro"
        >
          <X size={22} aria-hidden />
        </button>
      </header>
      {camadaQ.isLoading ? (
        <p role="status" className="py-6 text-text-secondary">
          Cargando camada…
        </p>
      ) : camadaQ.isError ? (
        <div className="space-y-3 py-4">
          <p role="alert">No se pudo cargar la camada. Tus datos se conservan.</p>
          <button type="button" className="ui-secondary" onClick={() => void camadaQ.refetch()}>
            Reintentar
          </button>
        </div>
      ) : !camada || camada.cerrada ? (
        <div className="space-y-4 py-4">
          <p>No hay una camada abierta para registrar.</p>
          {canManageAnimals && (
            <Link to="/camadas/nueva" className="ui-primary" onClick={onClose}>
              Crear camada
            </Link>
          )}
        </div>
      ) : (
        <>
          <p className="mb-5 text-sm text-text-secondary">
            <strong className="font-medium text-text-primary">{camada.codigo_lote}</strong> · Camada
            en curso
          </p>
          {!canManageAnimals ? (
            <Link className="ui-menu-row" to={`/camadas?camada=${camada.id}`} onClick={onClose}>
              Consultar camada <ChevronRight size={18} aria-hidden />
            </Link>
          ) : chooseNave ? (
            <>
              <button
                type="button"
                className="ui-text-button mb-2"
                onClick={() => setChooseNave(false)}
              >
                <ChevronLeft size={18} aria-hidden />
                Registros
              </button>
              {lotesQ.isLoading || navesQ.isLoading ? (
                <p role="status">Cargando naves…</p>
              ) : lotesQ.isError || navesQ.isError ? (
                <p role="alert">No se pudieron cargar las naves. Cierra y vuelve a intentarlo.</p>
              ) : !lotesQ.data?.length ? (
                <p>No hay naves asignadas a esta camada.</p>
              ) : (
                <div>
                  {lotesQ.data.map((lote) => (
                    <button
                      key={lote.id}
                      type="button"
                      className="ui-menu-row"
                      onClick={() => open(`/naves/${lote.nave_id}/pesajes/nuevo`)}
                    >
                      <Warehouse size={22} aria-hidden />
                      <span>
                        {navesQ.data?.find((n) => n.id === lote.nave_id)?.codigo ??
                          `Nave ${lote.nave_id}`}
                      </span>
                      <ChevronRight size={18} aria-hidden />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="ui-register-daily"
                onClick={() => open(`/camadas/${camada.id}/registro-diario`)}
              >
                <ClipboardList size={24} aria-hidden />
                <span>
                  <strong>Parte diario</strong>
                  <small>Bajas y observaciones de las naves</small>
                </span>
                <ChevronRight size={18} aria-hidden />
              </button>
              <div className="ui-register-grid">
                <button type="button" onClick={() => setChooseNave(true)}>
                  <Weight size={24} aria-hidden />
                  <span>Muestreo de peso</span>
                  <ChevronRight size={18} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => open(`/camadas/${camada.id}/entregas-pienso/nueva`)}
                >
                  <Package size={24} aria-hidden />
                  <span>Entrega de pienso</span>
                  <ChevronRight size={18} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => open(`/camadas/${camada.id}/salidas/nueva`, false)}
                >
                  <Truck size={24} aria-hidden />
                  <span>Salidas a matadero</span>
                  <ChevronRight size={18} aria-hidden />
                </button>
                {canManageAnimals && (
                  <button
                    type="button"
                    onClick={() => open(`/camadas/${camada.id}/entradas-animales/nueva`, false)}
                  >
                    <Bird size={24} aria-hidden />
                    <span>Entrada de animales</span>
                    <ChevronRight size={18} aria-hidden />
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </ModalOverlay>
  );
}
